import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, Ban, Info, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const MIN_AGE = 18;
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24h
const COOLDOWN_KEY = "lv_age_block_until";
const CURRENT_YEAR = new Date().getFullYear();

const MONTHS_IT = [
  { v: 1, l: "Gennaio" }, { v: 2, l: "Febbraio" }, { v: 3, l: "Marzo" },
  { v: 4, l: "Aprile" }, { v: 5, l: "Maggio" }, { v: 6, l: "Giugno" },
  { v: 7, l: "Luglio" }, { v: 8, l: "Agosto" }, { v: 9, l: "Settembre" },
  { v: 10, l: "Ottobre" }, { v: 11, l: "Novembre" }, { v: 12, l: "Dicembre" },
];

const daysInMonth = (year, month) => {
  if (!year || !month) return 31;
  return new Date(year, month, 0).getDate();
};

const calcAge = (y, m, d) => {
  if (!y || !m || !d) return null;
  const today = new Date();
  let age = today.getFullYear() - y;
  const beforeBirthday =
    today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d);
  if (beforeBirthday) age--;
  return age;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AuthPage = () => {
  const navigate = useNavigate();
  const { user, loading, loginWithPassword, register } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [regStep, setRegStep] = useState("gate"); // gate | credentials
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Age gate
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [blockedUntil, setBlockedUntil] = useState(null);

  useEffect(() => {
    if (!loading && user) {
      navigate(user.profile_completed ? "/dashboard" : "/setup-profilo", { replace: true });
    }
  }, [user, loading, navigate]);

  // Check cooldown on mount
  useEffect(() => {
    const until = parseInt(localStorage.getItem(COOLDOWN_KEY) || "0", 10);
    if (until && Date.now() < until) setBlockedUntil(until);
  }, []);

  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = CURRENT_YEAR; y >= CURRENT_YEAR - 120; y--) arr.push(y);
    return arr;
  }, []);

  const dayOptions = useMemo(() => {
    const max = daysInMonth(parseInt(birthYear, 10), parseInt(birthMonth, 10));
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [birthYear, birthMonth]);

  const computedAge = calcAge(
    parseInt(birthYear, 10), parseInt(birthMonth, 10), parseInt(birthDay, 10)
  );
  const birthDateComplete = !!birthDay && !!birthMonth && !!birthYear;
  const isoBirth = birthDateComplete
    ? `${birthYear}-${String(birthMonth).padStart(2, "0")}-${String(birthDay).padStart(2, "0")}`
    : "";

  const resetGate = () => {
    setBirthDay(""); setBirthMonth(""); setBirthYear(""); setError("");
  };

  const switchMode = (m) => {
    setMode(m); setError(""); setRegStep("gate"); resetGate();
  };

  const triggerBlock = () => {
    const until = Date.now() + COOLDOWN_MS;
    localStorage.setItem(COOLDOWN_KEY, String(until));
    setBlockedUntil(until);
    resetGate();
  };

  const handleGateContinue = () => {
    setError("");
    if (!birthDateComplete || computedAge === null) {
      setError("Inserisci la tua data di nascita completa.");
      return;
    }
    if (computedAge < MIN_AGE) {
      triggerBlock();
      return;
    }
    setRegStep("credentials");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      let data;
      if (mode === "register") {
        data = await register(email.trim(), password, name.trim(), isoBirth, true);
      } else {
        data = await loginWithPassword(email.trim(), password);
      }
      navigate(data.profile_completed ? "/dashboard" : "/setup-profilo", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string" && detail.startsWith("AGE_BLOCKED:")) {
        triggerBlock();
      } else {
        setError(formatApiErrorDetail(detail) || err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const tabBtn = (m, label) => (
    <button
      type="button"
      onClick={() => switchMode(m)}
      data-testid={`auth-tab-${m}`}
      className={`flex-1 py-3 font-bold uppercase tracking-wide text-sm border-2 border-[#0A0A0A] transition-colors ${
        mode === m ? "bg-[#0A0A0A] text-[#FDE047]" : "bg-white text-[#0A0A0A] hover:bg-gray-100"
      }`}
    >
      {label}
    </button>
  );

  const selectClass = "w-full bg-white border-2 border-[#0A0A0A] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#FDE047] font-medium appearance-none cursor-pointer";

  // Evita il flash del form di login al riavvio "a freddo": finché stiamo
  // verificando la sessione (o se sei già loggato e stiamo per reindirizzarti)
  // mostra uno spinner invece del form.
  if (loading || user) {
    return (
      <div
        className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"
        style={{ fontFamily: "'Outfit', sans-serif" }}
        data-testid="auth-loading"
      >
        <div className="w-12 h-12 border-4 border-[#FDE047] border-t-[#0A0A0A] rounded-full animate-spin"></div>
      </div>
    );
  }

  // ============ AGE BLOCKED SCREEN ============
  if (mode === "register" && blockedUntil && Date.now() < blockedUntil) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-10" style={{ fontFamily: "'Outfit', sans-serif" }}>
        <div className="w-full max-w-md">
          <Link to="/" className="block text-center text-2xl font-extrabold tracking-widest text-[#0A0A0A] mb-8" data-testid="auth-logo">LIBRIVICINI</Link>
          <div className="bg-white border-2 border-[#0A0A0A] shadow-[6px_6px_0px_#0A0A0A] p-6 sm:p-8 text-center" data-testid="age-blocked-card">
            <div className="w-16 h-16 bg-red-200 border-2 border-[#0A0A0A] rounded-full flex items-center justify-center mx-auto mb-4">
              <Ban size={30} className="text-[#0A0A0A]" />
            </div>
            <h1 className="text-xl font-black text-[#0A0A0A] mb-3">Registrazione non consentita</h1>
            <p className="text-sm text-gray-700 leading-relaxed mb-4" data-testid="age-blocked-message">
              Ci dispiace, in questa fase LibriVicini è riservato ai maggiorenni ({MIN_AGE}+).
              Non è possibile creare un account.
            </p>
            <div className="p-3 bg-[#FDFBF7] border-2 border-gray-200 rounded-lg text-left flex items-start gap-2 mb-5">
              <Info size={18} className="text-gray-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-gray-600">
                Per tutelare i dati personali e limitare gli adempimenti previsti per i minori,
                al lancio il servizio è aperto solo a chi ha almeno {MIN_AGE} anni.
              </p>
            </div>
            <button
              type="button"
              onClick={() => switchMode("login")}
              data-testid="age-blocked-back-btn"
              className="w-full bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] font-bold uppercase tracking-wide px-4 py-3 hover:bg-gray-800"
            >
              Torna all'accesso
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-10" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center text-2xl font-extrabold tracking-widest text-[#0A0A0A] mb-8" data-testid="auth-logo">
          LIBRIVICINI
        </Link>

        <div className="bg-white border-2 border-[#0A0A0A] shadow-[6px_6px_0px_#0A0A0A] p-6 sm:p-8">
          <div className="flex gap-0 mb-6">
            {tabBtn("login", "Accedi")}
            {tabBtn("register", "Registrati")}
          </div>

          {/* ===== REGISTER · STEP 1: AGE GATE ===== */}
          {mode === "register" && regStep === "gate" && (
            <div className="space-y-4" data-testid="age-gate">
              <div className="p-4 bg-[#EFF6FF] border-2 border-[#0A0A0A] rounded-lg flex items-start gap-3" data-testid="age-gate-explainer">
                <ShieldCheck size={22} className="text-[#0A0A0A] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#0A0A0A] leading-relaxed">
                  <strong>Prima di iniziare, inserisci la tua data di nascita.</strong> Ci serve
                  per verificare l'età: in questa fase di lancio LibriVicini è riservato ai
                  <strong> maggiorenni ({MIN_AGE}+)</strong>, così possiamo tutelare al meglio i tuoi
                  dati e mantenere il servizio semplice e sicuro.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">Data di nascita</label>
                <div className="grid grid-cols-3 gap-2">
                  <select value={birthDay} onChange={(e) => { setBirthDay(e.target.value); setError(""); }} className={selectClass} data-testid="gate-birth-day">
                    <option value="">Giorno</option>
                    {dayOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select value={birthMonth} onChange={(e) => { setBirthMonth(e.target.value); setError(""); }} className={selectClass} data-testid="gate-birth-month">
                    <option value="">Mese</option>
                    {MONTHS_IT.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                  <select value={birthYear} onChange={(e) => { setBirthYear(e.target.value); setError(""); }} className={selectClass} data-testid="gate-birth-year">
                    <option value="">Anno</option>
                    {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>

              {error && <p className="text-sm text-red-600 font-medium" data-testid="auth-error">{error}</p>}

              <button
                type="button"
                onClick={handleGateContinue}
                data-testid="gate-continue-btn"
                className="w-full bg-[#FDE047] text-[#0A0A0A] border-2 border-[#0A0A0A] font-bold uppercase tracking-wide px-4 py-3 shadow-[4px_4px_0px_#0A0A0A] hover:bg-yellow-300 transition-transform active:translate-y-1 active:translate-x-1 active:shadow-none"
              >
                Continua
              </button>
              <p className="text-xs text-gray-500 leading-relaxed">
                La data di nascita è conservata in modo sicuro e non viene mai mostrata pubblicamente.
              </p>
            </div>
          )}

          {/* ===== REGISTER · STEP 2: CREDENTIALS  /  LOGIN ===== */}
          {(mode === "login" || (mode === "register" && regStep === "credentials")) && (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
              {mode === "register" && (
                <>
                  <button
                    type="button"
                    onClick={() => { setRegStep("gate"); setError(""); }}
                    data-testid="back-to-gate-btn"
                    className="flex items-center gap-1 text-sm text-[#0A0A0A] font-bold hover:text-gray-600"
                  >
                    <ArrowLeft size={16} /> Data di nascita
                  </button>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Nome</label>
                    <input
                      type="text" name="name" autoComplete="name" value={name}
                      onChange={(e) => { setName(e.target.value); if (error) setError(""); }}
                      placeholder="Come ti chiami" data-testid="auth-name-input"
                      className="w-full border-2 border-[#0A0A0A] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#FDE047]"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-semibold mb-1">Email</label>
                <input
                  type="email" name="email" autoComplete="email" required value={email}
                  onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                  placeholder="tua@email.it" data-testid="auth-email-input"
                  className="w-full border-2 border-[#0A0A0A] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#FDE047]"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"} name="password"
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                    required value={password}
                    onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
                    placeholder={mode === "register" ? "Almeno 8 caratteri" : "La tua password"}
                    data-testid="auth-password-input"
                    className="w-full border-2 border-[#0A0A0A] px-3 py-2.5 pr-11 focus:outline-none focus:ring-2 focus:ring-[#FDE047]"
                  />
                  <button
                    type="button" onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                    data-testid="auth-toggle-password"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#0A0A0A] hover:text-gray-500"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {mode === "register" && (
                <div className="p-3 bg-[#FEF3C7] border-2 border-[#FBBF24] rounded-lg" data-testid="age-declaration">
                  <p className="text-xs text-[#92400E] font-medium leading-relaxed">
                    Cliccando su <strong>Registrati</strong> dichiari, sotto la tua esclusiva
                    responsabilità, di essere <strong>maggiorenne (18+)</strong>. L'inserimento di
                    dati falsi costituisce una violazione dei Termini di Servizio e comporterà la
                    cancellazione immediata dell'account.
                  </p>
                </div>
              )}

              {error && <p className="text-sm text-red-600 font-medium" data-testid="auth-error">{error}</p>}

              <button
                type="submit" disabled={busy} data-testid="auth-submit-btn"
                className="w-full bg-[#FDE047] text-[#0A0A0A] border-2 border-[#0A0A0A] font-bold uppercase tracking-wide px-4 py-3 shadow-[4px_4px_0px_#0A0A0A] hover:bg-yellow-300 transition-transform active:translate-y-1 active:translate-x-1 active:shadow-none disabled:opacity-60"
              >
                {busy ? "Attendi..." : mode === "register" ? "Registrati" : "Accedi"}
              </button>
            </form>
          )}

          {mode === "login" && (
            <div className="mt-4 text-center">
              <Link to="/password-dimenticata" className="text-sm text-[#0A0A0A] underline hover:no-underline" data-testid="forgot-password-link">
                Password dimenticata?
              </Link>
            </div>
          )}

          {mode === "register" && (
            <p className="mt-4 text-xs text-gray-500 leading-relaxed">
              Registrandoti accetti i <Link to="/termini" className="underline">Termini</Link> e la{" "}
              <Link to="/privacy" className="underline">Privacy</Link>. Servizio riservato ai maggiorenni ({MIN_AGE}+).
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
